import React, { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/solid';

/**
 * ChatInput - Modern chat input with auto-expand and send button
 */
const ChatInput = ({ onSend, sending = false, placeholder = 'Type a message...' }) => {
    const [message, setMessage] = useState('');
    const textareaRef = useRef(null);

    // Auto-resize textarea
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
        }
    }, [message]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!message.trim() || sending) return;
        onSend?.(message.trim());
        setMessage('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="relative">
            <div className="flex items-end gap-2 p-3 bg-white border border-slate-200 rounded-2xl shadow-sm">
                {/* Attachment button */}
                <button
                    type="button"
                    className="p-2 text-slate-400 hover:text-indigo-500 hover:bg-slate-50 rounded-xl transition-colors flex-shrink-0"
                    title="Attach file"
                >
                    <PaperClipIcon className="h-5 w-5" />
                </button>

                {/* Text input */}
                <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    rows={1}
                    className="flex-1 resize-none border-0 bg-transparent text-sm text-slate-900 placeholder-slate-400 focus:ring-0 focus:outline-none max-h-[150px]"
                />

                {/* Character count */}
                {message.length > 100 && (
                    <span className={`text-xs flex-shrink-0 ${message.length > 500 ? 'text-rose-500' : 'text-slate-400'}`}>
                        {message.length}
                    </span>
                )}

                {/* Send button */}
                <button
                    type="submit"
                    disabled={!message.trim() || sending}
                    className={`
                        p-2 rounded-xl transition-all duration-200 flex-shrink-0
                        ${message.trim() && !sending
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg hover:-translate-y-0.5'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'}
                    `}
                >
                    {sending ? (
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <PaperAirplaneIcon className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Keyboard hint */}
            <p className="text-xs text-slate-400 mt-2 text-center">
                Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500 font-mono">Enter</kbd> to send,
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500 font-mono ml-1">Shift+Enter</kbd> for new line
            </p>
        </form>
    );
};

export default ChatInput;
